import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { PropTypes } from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Loader } from '@ge/components/loader';
import { Tbody, Td, Tr } from '@ge/components/table';
import { useTableFactories } from '@ge/components/table/use-table-factories';

import { ReportWidgetTable } from '../../tables/report-widget-table';

import { AssetTableColumns, AssetTableDefs } from './asset-table-col';

dayjs.extend(isoWeek);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${({ summaryColumn }) =>
    summaryColumn &&
    `
    ${Tbody} ${Tr} ${Td}:last-of-type {
      font-weight: 500;
    }
  `}

  ${({ theme }) =>
    `${Tbody} ${Tr}:last-of-type ${Td} {
      border-top: 1px solid ${theme.createReport.widget.productionTable.rowBorderColor};
      font-weight: 700;
    }
  `}
`;

export const AssetTableWidget = ({ name, columns, data = [], isEditing, isInteractive }) => {
  const { t, ready } = useTranslation(['reporting.widgets']);
  // initialize all rows being selected
  const [rowsSelected] = useState(() => data.map(({ id }) => id));

  const cellValueMapFn = useCallback(
    (cellValue) => {
      return {
        [AssetTableColumns.ASSETS]:
          cellValue.padNumber === AssetTableColumns.ASSET_TOTAL
            ? t('asset_table.asset_totals', 'Total')
            : cellValue.padNumber,
        [AssetTableColumns.PRODUCTION]: cellValue.assetActualProdKpi
          ? parseFloat(cellValue.assetActualProdKpi).toFixed(2)
          : '0.00',
        [AssetTableColumns.LOST_PRODUCTION]: cellValue.assetLostProdKpi
          ? parseFloat(cellValue.assetLostProdKpi).toFixed(2)
          : '0.00',
        [AssetTableColumns.CONTRACTUAL_AVAILABILITY]: cellValue.assetAvailabilityKpi
          ? parseFloat(cellValue.assetAvailabilityKpi).toFixed(2)
          : '0.00',
      };
    },
    [t],
  );

  // Table factories
  const [columnGroupFactory, columnFactory, cellFactory] = useTableFactories({
    t,
    columnDefs: AssetTableDefs,
    cellValueMapFn,
  });

  if (!ready) {
    return <Loader />;
  }

  return (
    <Container>
      <ReportWidgetTable
        key={name}
        compressed
        noTitles
        scrollable={isInteractive}
        transparent
        columnGroupFactory={columnGroupFactory}
        columnFactory={columnFactory}
        cellFactory={cellFactory}
        columns={columns}
        values={data}
        onValueSelect={() => null}
        rowsSelected={rowsSelected}
        rowKeyProperty={'id'}
        isEditing={isEditing}
        variableHeight={true}
        vAlign={'middle'}
      />
    </Container>
  );
};

AssetTableWidget.propTypes = {
  name: PropTypes.string.isRequired,
  data: PropTypes.instanceOf(Object),
  columns: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  isEditing: PropTypes.bool,
  isInteractive: PropTypes.bool,
};
